// Persistencia del registro del día. Offline-first:
//   - Fuente inmediata: device-local (localStorage), sin expiración. Funciona
//     sin conexión y sobrevive recargas.
//   - Sync: Supabase (tabla registros_turno, RLS por usuario). Best-effort:
//     si falla la red, lo local sigue intacto.
//
// El registro clínico es estructurado/des-identificado + notas filtradas. Es un
// apoyo personal, NO el registro oficial de la institución.

import { supabase } from './supabase';
// Catálogo cerrado de aislamientos: se importa (no se copia) porque este
// módulo no tiene ciclo con registroTurno.js y un tercer catálogo sería una
// divergencia esperando ocurrir.
import { AISLAMIENTOS } from './registroTurno';

const PREFIJO = 'mipae:registro-turno:';

function clave(userId) {
  return `${PREFIJO}${userId || 'anon'}`;
}

// ── Local ─────────────────────────────────────────────────────────────────────
export function cargarRegistros(userId) {
  try {
    const raw = localStorage.getItem(clave(userId));
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// ── Cama activa del turno (para la captura rápida desde cualquier pantalla) ────
// La fricción real del piloto: "salgo de la pieza y me meto a MiPAE en turno a
// registrar" es un embudo porque la app pierde la pista del paciente apenas
// sales de la ficha. Esto DERIVA (no persiste aparte, cero staleness) cuál es
// la cama que estás trabajando ahora: el registro abierto (no cerrado, no de
// práctica) tocado más recientemente. Con esto, Home y Benito pueden ofrecer
// "registrar en la cama X" en un toque desde donde estés. Pura y testeable.
export function camaActivaTurno(registros = []) {
  const vivos = (Array.isArray(registros) ? registros : [])
    .filter((r) => r && !r.cerrado && !r.practica && (r.cama || '').toString().trim());
  if (!vivos.length) return null;
  const ts = (r) => Number(r.updated_at || r.creado || 0);
  const elegido = vivos.reduce((mejor, r) => (ts(r) >= ts(mejor) ? r : mejor));
  return { id: elegido.id, cama: String(elegido.cama).trim() };
}

// ── Preferencia: auto-foco de MiPAE en turno al iniciar (por dispositivo) ───────────
// ON POR DEFECTO desde el 2026-07-31 (dictado del fundador: el delta que mata
// la adopción es abrir la app y LLEGAR a hacer lo que viniste a hacer; el
// auto-foco opt-in estaba escondido y nadie lo encontraba). '0' explícito lo
// apaga (el switch del perfil sigue mandando); cualquier otro valor = activo.
function claveAutoFoco(userId) {
  return `autoFocoTurno_${userId || 'anon'}`;
}
export function getAutoFocoTurno(userId) {
  try { return localStorage.getItem(claveAutoFoco(userId)) !== '0'; } catch { return true; }
}
export function setAutoFocoTurno(userId, valor) {
  try { localStorage.setItem(claveAutoFoco(userId), valor ? '1' : '0'); } catch { /* noop */ }
}

// ── Cierre del turno: "ya lo vi hoy" (device-local, por fecha) ─────────────────
// El cierre del día se muestra al terminar el turno; una vez que el usuario lo
// descarta, no vuelve a aparecer ese mismo día (no naggear). Se re-arma solo al
// día siguiente porque la clave guarda la fecha.
export function fechaHoyKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function claveCierre(userId) {
  return `mipae:cierre-turno-visto:${userId || 'anon'}`;
}
export function getCierreTurnoVisto(userId) {
  try { return localStorage.getItem(claveCierre(userId)) || null; } catch { return null; }
}
export function setCierreTurnoVisto(userId, fecha) {
  try { localStorage.setItem(claveCierre(userId), fecha || fechaHoyKey()); } catch { /* noop */ }
}

export function guardarRegistros(userId, registros) {
  try {
    localStorage.setItem(clave(userId), JSON.stringify(registros || []));
    return true;
  } catch {
    return false;
  }
}

// ── Escrituras amortiguadas ───────────────────────────────────────────────────
// El tipeo dispara un cambio de estado por tecla; serializar el turno COMPLETO
// a localStorage (síncrono, en main thread) y subir un upsert a Supabase en
// cada tecla congela la UI en un teléfono con red hospitalaria (auditoría
// 2026-07-27). Amortiguación trailing con latencia acotada: la PRIMERA
// escritura de la ventana arma el timer y al vencer se escribe el estado más
// reciente (una escritura por ventana, nunca más de `ms` de atraso).
// `flushEscriturasTurno` descarga lo pendiente al desmontar el módulo y en
// pagehide, para no perder la última tecla al cerrar la pestaña.
let localPendiente = null; // { userId, registros } más reciente sin escribir
let localTimer = null;
export function guardarRegistrosDebounced(userId, registros, ms = 400) {
  localPendiente = { userId, registros };
  if (localTimer) return;
  localTimer = setTimeout(() => {
    localTimer = null;
    if (localPendiente) {
      guardarRegistros(localPendiente.userId, localPendiente.registros);
      localPendiente = null;
    }
  }, ms);
}

const pushPendiente = new Map(); // id → registro más reciente sin subir
const pushTimers = new Map(); // id → timer
export function pushRemotoDebounced(registro, ms = 1500) {
  if (!registro || !registro.id || registro.practica) return; // la práctica jamás sube
  pushPendiente.set(registro.id, registro);
  if (pushTimers.has(registro.id)) return;
  pushTimers.set(registro.id, setTimeout(() => {
    pushTimers.delete(registro.id);
    const r = pushPendiente.get(registro.id);
    pushPendiente.delete(registro.id);
    if (r) pushRemoto(r);
  }, ms));
}

// Devuelve la promesa de los push disparados. Los callers de siempre (el
// cleanup del módulo, el pagehide) la ignoran y siguen igual; la usa el cierre
// de sesión, que necesita ESPERAR a que suban antes de barrer el dispositivo:
// si se va primero la sesión, el upsert llega sin token y lo escrito se pierde.
// `pushRemoto` devuelve true/false sin lanzar, así que nunca rechaza.
export function flushEscriturasTurno() {
  if (localTimer) { clearTimeout(localTimer); localTimer = null; }
  if (localPendiente) {
    guardarRegistros(localPendiente.userId, localPendiente.registros);
    localPendiente = null;
  }
  for (const t of pushTimers.values()) clearTimeout(t);
  pushTimers.clear();
  const subidas = Array.from(pushPendiente.values()).map((r) => pushRemoto(r));
  pushPendiente.clear();
  return Promise.all(subidas);
}
// pagehide cubre cierre de pestaña y bfcache (más confiable que beforeunload
// en móvil, donde vive el uso real del módulo).
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushEscriturasTurno);
}

// ── Parche device-local de UN registro, directo a disco ───────────────────────
// Escribe sin pasar por el estado de React. Existe para el flush de desmontaje
// del borrador de la barra de captura: en ese momento el estado del módulo puede
// estar desapareciendo, así que no se puede depender de un setState que quizá no
// llegue a procesarse (el orden de los cleanups padre/hijo no es algo en lo que
// convenga apostar el texto que la enfermera alcanzó a escribir).
//
// Descarga primero lo amortiguado: sin eso leería un localStorage viejo y
// devolvería el turno a ese estado, perdiendo lo que estaba en la ventana del
// debounce. Solo para claves device-local: no toca el remoto ni `updated_at`.
export function parchearRegistroLocal(userId, id, patch) {
  if (!id || !patch || typeof patch !== 'object') return false;
  flushEscriturasTurno();
  const regs = cargarRegistros(userId);
  let tocado = false;
  const next = regs.map((r) => {
    if (!r || r.id !== id) return r;
    tocado = true;
    return { ...r, ...patch };
  });
  if (!tocado) return false;
  return guardarRegistros(userId, next);
}

// ── Estado de la ronda 2 h (device-local, efímero por turno) ──────────────────
// La ronda viva (hora de inicio + ítems marcados por paciente) sobrevive a que
// se salga del módulo o se recargue, para que la enfermera la retome donde la
// dejó. Es transitoria: se descarta al cerrar el turno o cuando queda vieja.
function claveRonda(userId) {
  return `mipae:ronda:${userId || 'anon'}`;
}
export function cargarRonda(userId) {
  try {
    const raw = localStorage.getItem(claveRonda(userId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data === 'object' && data.inicio ? data : null;
  } catch {
    return null;
  }
}
export function guardarRonda(userId, ronda) {
  try {
    localStorage.setItem(claveRonda(userId), JSON.stringify(ronda || {}));
    return true;
  } catch {
    return false;
  }
}
export function limpiarRonda(userId) {
  try { localStorage.removeItem(claveRonda(userId)); } catch { /* noop */ }
}

// ── Forma de una fila remota ──────────────────────────────────────────────────
// `aLocal` produce EXACTAMENTE estas claves: son la forma completa de una fila de
// `registros_turno`. Cualquier OTRA clave del registro es device-local, o sea
// vive solo en el teléfono y el remoto nunca la vio (`tareas`, `indicaciones`,
// `practica`, `borradorNota`…).
//
// Es un CONTRATO, verificado por test contra la salida real de `aLocal`: si
// alguien agrega una columna y se olvida de declararla aquí, el test falla en vez
// de que el bug aparezca en el teléfono de una enfermera.
export const CLAVES_REMOTAS = new Set([
  'id', 'cama', 'turno', 'cerrado', 'guardado', 'gravedad', 'aislamiento',
  'aislamientos', 'aislamientoMotivo', 'sfVolumen', 'dispositivos', 'entradas', 'evolucion',
  'examenFisico', 'visitaEnfermeria', 'visitaTecnica', 'examenPrevio',
  'trayectoria', 'diaDesdeUno',
  'creado', 'updated_at',
]);

// ── Regla de preservación del merge ───────────────────────────────────────────
// El remoto solo puede pisar lo que TIENE. Si su valor para una clave es nulo o
// no viene, no es una opinión ("se borró"), es silencio ("no sé de eso"), y manda
// lo del teléfono.
//
// Nació de un bug real del piloto: `visitaEnfermeria`, `visitaTecnica` y
// `examenPrevio` se escribían en la raíz del registro sin tener columna, el
// remoto llegaba sin ellas y gana los empates de `updated_at` (el caso NORMAL,
// porque el remoto se escribió desde ese mismo local), así que se destruían en
// cada reentrada al módulo. Antes se restauraban a mano clave por clave en
// RegistroTurno.jsx y esa lista se quedó atrás dos veces.
//
// La regla es nulo-o-ausente, no solo ausente, y eso es deliberado: cubre
// también la TRANSICIÓN de una clave que acaba de recibir columna (migración
// 20260730000000). Ahí el teléfono ya tiene la visita llena y la fila remota
// todavía la tiene en null; con una regla de "solo si falta la clave", el primer
// arranque después del deploy la habría borrado.
//
// Contrapartida asumida a conciencia: si otro dispositivo BORRA un campo a
// propósito (por ejemplo "empezar de nuevo la evolución") y este teléfono todavía
// lo tiene, el campo revive. Se eligió ese lado porque revivir es visible y se
// vuelve a borrar en un toque, mientras que perder la visita de enfermería del
// turno es silencioso y no se recupera. El arreglo de fondo es merge campo a
// campo en vez de por registro entero, que es otra conversación (ver
// analisis/cuellos-botella-mi-turno.md §4.5).
function preservarLocal(remoto, local) {
  if (!local) return remoto;
  let extra = null;
  for (const k of Object.keys(local)) {
    const vLocal = local[k];
    if (vLocal === undefined || vLocal === null) continue;
    const vRemoto = remoto[k];
    if (vRemoto !== undefined && vRemoto !== null) continue;
    if (!extra) extra = {};
    extra[k] = vLocal;
  }
  return extra ? { ...remoto, ...extra } : remoto;
}

// ── Merge ───────────────────────────────────────────────────────────────────
// Une por id; ante conflicto gana el de updated_at más reciente (el remoto gana
// los empates: es la fuente de sync de la forma compartida), pero solo en lo que
// el remoto de verdad trae (ver `preservarLocal`). Pura y testeable (no toca red
// ni disco).
export function mergeRegistros(locales = [], remotos = []) {
  const porId = new Map();
  const localPorId = new Map();
  for (const r of Array.isArray(locales) ? locales : []) {
    if (!r || !r.id) continue;
    localPorId.set(r.id, r);
    const prev = porId.get(r.id);
    if (!prev || (r.updated_at || 0) >= (prev.updated_at || 0)) porId.set(r.id, r);
  }
  for (const r of Array.isArray(remotos) ? remotos : []) {
    if (!r || !r.id) continue;
    const prev = porId.get(r.id);
    if (!prev || (r.updated_at || 0) >= (prev.updated_at || 0)) {
      porId.set(r.id, preservarLocal(r, localPorId.get(r.id)));
    }
  }
  return Array.from(porId.values()).sort((a, b) => (b.creado || 0) - (a.creado || 0));
}

// ── Remoto (Supabase) ─────────────────────────────────────────────────────────
// Columnas de la tabla, en el orden del SELECT. Vive aparte para que el SELECT y
// el contrato de CLAVES_REMOTAS no se puedan desalinear en silencio.
const COLUMNAS = 'id, cama, turno, cerrado, guardado, gravedad, aislamiento, aislamientos, aislamiento_motivo, sf_volumen, dispositivos, entradas, evolucion, examen_fisico, visita_enfermeria, visita_tecnica, examen_previo, trayectoria, dia_desde_uno, creado, updated_at';

export function aLocal(row) {
  return {
    id: row.id,
    cama: row.cama || '',
    turno: row.turno ?? null,
    cerrado: !!row.cerrado,
    guardado: !!row.guardado,
    gravedad: row.gravedad ?? null,
    aislamiento: row.aislamiento ?? 'Ninguno',
    // Catálogo cerrado también AL LEER: la columna tiene su CHECK, pero un
    // jsonb/array adulterado jamás afirma un aislamiento que la app no conoce.
    aislamientos: Array.isArray(row.aislamientos)
      ? row.aislamientos.filter((a) => AISLAMIENTOS.includes(a) && a !== 'Ninguno')
      : [],
    aislamientoMotivo: row.aislamiento_motivo ?? '',
    sfVolumen: row.sf_volumen ?? 10,
    dispositivos: Array.isArray(row.dispositivos) ? row.dispositivos : [],
    entradas: Array.isArray(row.entradas) ? row.entradas : [],
    evolucion: row.evolucion ?? null,
    // El examen de ingreso viaja al remoto (a diferencia de `tareas`): su
    // valor es la evidencia con hora, y la evidencia debe sobrevivir al
    // dispositivo (migración 20260707120000).
    examenFisico: row.examen_fisico ?? null,
    // Mismo criterio, migración 20260730000000: las visitas y el examen previo
    // son evidencia con hora y tienen que seguir a la CUENTA, no al teléfono
    // ("lo que escribo en el celular tiene que estar si lo busco del PC").
    visitaEnfermeria: row.visita_enfermeria ?? null,
    visitaTecnica: row.visita_tecnica ?? null,
    examenPrevio: row.examen_previo ?? null,
    // Trayectoria del turno (E4): declaraciones con hora y ts, catálogo
    // cerrado. Evidencia que sigue a la cuenta, mismo criterio que las visitas.
    trayectoria: Array.isArray(row.trayectoria) ? row.trayectoria : null,
    // Desde qué día cuenta sus dispositivos (migración 20261010120000). Solo
    // `true` afirma algo; NULL (fila de antes o de la app sin actualizar) queda
    // en null, que el merge trata como silencio y se lee como día 0.
    diaDesdeUno: row.dia_desde_uno === true ? true : null,
    creado: row.creado ? new Date(row.creado).getTime() : Date.now(),
    updated_at: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
  };
}

// Exportada solo para test (el filtro del catálogo al SUBIR se fija ahí).
export function aRemoto(r) {
  return {
    id: r.id,
    cama: r.cama || null,
    turno: r.turno ?? null,
    cerrado: !!r.cerrado,
    guardado: !!r.guardado,
    gravedad: r.gravedad ?? null,
    aislamiento: r.aislamiento ?? 'Ninguno',
    // Mismo filtro que al leer: una lista con basura chocaría con el CHECK de
    // la BD y el upsert ENTERO fallaría en silencio, notas clínicas incluidas.
    aislamientos: Array.isArray(r.aislamientos)
      ? r.aislamientos.filter((a) => AISLAMIENTOS.includes(a) && a !== 'Ninguno')
      : [],
    aislamiento_motivo: r.aislamientoMotivo || null,
    sf_volumen: r.sfVolumen ?? 10,
    dispositivos: r.dispositivos || [],
    entradas: r.entradas || [],
    evolucion: r.evolucion ?? null,
    examen_fisico: r.examenFisico ?? null,
    visita_enfermeria: r.visitaEnfermeria ?? null,
    visita_tecnica: r.visitaTecnica ?? null,
    examen_previo: r.examenPrevio ?? null,
    trayectoria: r.trayectoria ?? null,
    // Solo se manda cuando es true: la forma de contar se fija al crear el
    // registro y jamás vuelve atrás, ni siquiera desde un teléfono que no la
    // tenga (un upsert sin la columna no la toca).
    ...(r.diaDesdeUno === true ? { dia_desde_uno: true } : {}),
    creado: new Date(r.creado || Date.now()).toISOString(),
    updated_at: new Date(r.updated_at || Date.now()).toISOString(),
  };
}

// Trae los registros del usuario. Devuelve [] (no lanza) si no hay sesión o red.
export async function fetchRemotos() {
  try {
    const { data, error } = await supabase
      .from('registros_turno')
      .select(COLUMNAS);
    if (error || !Array.isArray(data)) return [];
    return data.map(aLocal);
  } catch {
    return [];
  }
}

// Upsert best-effort de un registro. Devuelve true/false sin lanzar.
export async function pushRemoto(registro) {
  try {
    const { error } = await supabase
      .from('registros_turno')
      .upsert(aRemoto(registro), { onConflict: 'id' });
    return !error;
  } catch {
    return false;
  }
}

export async function borrarRemoto(id) {
  try {
    const { error } = await supabase.from('registros_turno').delete().eq('id', id);
    return !error;
  } catch {
    return false;
  }
}
